import * as AWS from 'aws-sdk/global';
import S3 from 'aws-sdk/clients/s3';
import { gql, Resolver } from '@apollo/client';

interface DownloadMultipleFromS3Args {
    bucketName: string;
    bucketRegion: string;
    identityPoolId: string;
    keys: string[];
    partialResults: boolean;
}

export const downloadMultipleFromS3: Resolver = async (
    obj: Record<string, unknown>,
    args: DownloadMultipleFromS3Args,
): Promise<DownloadResult[]> => {
    const { bucketName, bucketRegion, identityPoolId, keys, partialResults } =
        args;

    if (keys.length === 0) {
        const error = Error('No keys were specified for download');

        return Promise.reject(error);
    }

    const credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
    });

    AWS.config.update({
        region: bucketRegion,
        credentials,
    });

    const s3 = new S3({
        params: { Bucket: bucketName },
        region: bucketRegion,
        credentials,
    });

    const allResults = keys.map(async (key: string) => {
        const params = {
            Bucket: bucketName,
            Key: key,
        };

        return s3
            .getObject(params)
            .promise()
            .then((data: S3.GetObjectOutput) => {
                const results: DownloadResult = {
                    key,
                    raw: null,
                    parsed: null,
                };
                const responseBody =
                    (data.Body as Uint8Array) ?? new TextEncoder().encode('');

                if (key.match(/^.*\.(json|JSON)$/)) {
                    try {
                        results.parsed = JSON.parse(
                            new TextDecoder('utf-8').decode(responseBody),
                        );
                    } catch {
                        results.raw = responseBody;
                    }
                } else {
                    results.raw = responseBody;
                }

                return Promise.resolve(results);
            })
            .catch(err => Promise.reject(err));
    });

    if (partialResults) {
        const settledResults = await Promise.allSettled(allResults);

        return settledResults.map(
            (
                result:
                    | PromiseSettledResult<DownloadResult>
                    | PromiseFulfilledResult<DownloadResult>
                    | PromiseRejectedResult,
            ) => 'value' in result ? result.value : result.reason,
        );
    }

    return Promise.all(allResults);
};

export const DOWNLOAD_MULTIPLE_FROM_S3 = gql`
    query DownloadMultipleFromS3(
        $bucketName: String
        $bucketRegion: String
        $identityPoolId: String
        $keys: [String]
        $partialResults: Boolean
    ) {
        downloadMultipleFromS3(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            identityPoolId: $identityPoolId
            keys: $keys
            partialResults: $partialResults
        ) @client
    }
`;
